import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { API_BASE_ISSUERSERVICE } from 'src/environments/api';
import {
    ApiResponse,
    CreateCredentialDefinitionWithAttestationRequest,
    CredentialDefinitionActivationRequest,
    CredentialDefinitionDto,
    CredentialDefinitionUpdate,
    CredentialDefinitionWithAttestation,
    CredentialOffer,
    CredentialRequestMessage,
    CredentialRequestStatus,
    Holder,
    HolderRequest,
    IssuerAttestationDefinition,
    IssuerCredentialDefinition,
    IssuerCredentialDefinitionRequest,
    IssuerCredentialResource,
    IssuerCredentialStatus,
    IssuerMetadata,
    IssuerRequestOptions,
    JsonObject,
    QuerySpec,
    UpdateCredentialDefinitionRequest,
    VerifiableCredentialResource
} from '../models/issuer-api.models';

@Injectable({ providedIn: 'root' })
export class IssuerService {
    private readonly apiBase = `${API_BASE_ISSUERSERVICE}/v1beta`;

    constructor(private readonly http: HttpClient) {}

    createCredentialDefinition(
        participantContextId: string,
        request: IssuerCredentialDefinitionRequest,
        options?: IssuerRequestOptions
    ): Observable<ApiResponse<CredentialDefinitionDto>> {
        return this.http.post<ApiResponse<CredentialDefinitionDto>>(
            this.credentialDefinitionsUrl(participantContextId),
            request,
            this.httpOptions(options)
        );
    }

    createCredentialDefinitionWithAttestation(
        participantContextId: string,
        request: CreateCredentialDefinitionWithAttestationRequest,
        options?: IssuerRequestOptions
    ): Observable<ApiResponse<CredentialDefinitionWithAttestation>> {
        return this.http.post<ApiResponse<CredentialDefinitionWithAttestation>>(
            `${this.credentialDefinitionsUrl(participantContextId)}/with-attestation`,
            request,
            this.httpOptions(options)
        );
    }

    getCredentialDefinition(
        participantContextId: string,
        credentialDefinitionId: string,
        options?: IssuerRequestOptions
    ): Observable<ApiResponse<IssuerCredentialDefinition>> {
        return this.http.get<ApiResponse<IssuerCredentialDefinition>>(
            `${this.credentialDefinitionsUrl(participantContextId)}/${this.segment(credentialDefinitionId)}`,
            { headers: this.headers(options) }
        );
    }

    queryCredentialDefinitions(
        participantContextId: string,
        query: QuerySpec | null = null,
        options?: IssuerRequestOptions
    ): Observable<ApiResponse<IssuerCredentialDefinition[]>> {
        return this.http.post<ApiResponse<IssuerCredentialDefinition[]>>(
            `${this.credentialDefinitionsUrl(participantContextId)}/query`,
            query,
            { headers: this.headers(options) }
        );
    }

    updateCredentialDefinition(
        participantContextId: string,
        request: UpdateCredentialDefinitionRequest,
        options?: IssuerRequestOptions
    ): Observable<ApiResponse<CredentialDefinitionUpdate>> {
        return this.http.put<ApiResponse<CredentialDefinitionUpdate>>(
            this.credentialDefinitionsUrl(participantContextId),
            request,
            this.httpOptions(options)
        );
    }

    deleteCredentialDefinition(
        participantContextId: string,
        credentialDefinitionId: string,
        options?: IssuerRequestOptions
    ): Observable<ApiResponse<void>> {
        return this.http.delete<ApiResponse<void>>(
            `${this.credentialDefinitionsUrl(participantContextId)}/${this.segment(credentialDefinitionId)}`,
            this.httpOptions(options)
        );
    }

    setCredentialDefinitionActive(
        participantContextId: string,
        credentialDefinitionId: string,
        request: CredentialDefinitionActivationRequest,
        options?: IssuerRequestOptions
    ): Observable<ApiResponse<CredentialDefinitionDto>> {
        return this.http.put<ApiResponse<CredentialDefinitionDto>>(
            `${this.credentialDefinitionsUrl(participantContextId)}/${this.segment(credentialDefinitionId)}/active`,
            request,
            this.httpOptions(options)
        );
    }

    createCredentialDefinitionFromTurtle(
        participantContextId: string,
        label: string,
        turtle: string,
        options?: IssuerRequestOptions
    ): Observable<ApiResponse<CredentialDefinitionDto>> {
        const params = new HttpParams().set('label', label);
        return this.http.post<ApiResponse<CredentialDefinitionDto>>(
            this.credentialDefinitionsUrl(participantContextId),
            turtle,
            {
                headers: this.headers(options).set('Content-Type', 'text/turtle'),
                params
            }
        );
    }

    getCredentialDefinitionShacl(
        participantContextId: string,
        credentialDefinitionId: string,
        options?: IssuerRequestOptions
    ): Observable<string> {
        return this.http.get(
            `${this.credentialDefinitionsUrl(participantContextId)}/${this.segment(credentialDefinitionId)}/shacl`,
            {
                headers: this.headers(options).set('Accept', 'text/turtle'),
                responseType: 'text'
            }
        );
    }

    createHolder(
        participantContextId: string,
        request: HolderRequest,
        options?: IssuerRequestOptions
    ): Observable<ApiResponse<Holder>> {
        return this.http.post<ApiResponse<Holder>>(
            this.holdersUrl(participantContextId),
            request,
            this.httpOptions(options)
        );
    }

    updateHolder(
        participantContextId: string,
        request: HolderRequest,
        options?: IssuerRequestOptions
    ): Observable<ApiResponse<Holder>> {
        return this.http.put<ApiResponse<Holder>>(
            this.holdersUrl(participantContextId),
            request,
            this.httpOptions(options)
        );
    }

    getAllHolders(
        participantContextId: string,
        options?: IssuerRequestOptions
    ): Observable<ApiResponse<Holder[]>> {
        return this.http.get<ApiResponse<Holder[]>>(
            this.holdersUrl(participantContextId),
            { headers: this.headers(options) }
        );
    }

    getHolderById(
        participantContextId: string,
        holderId: string,
        options?: IssuerRequestOptions
    ): Observable<ApiResponse<Holder>> {
        return this.http.get<ApiResponse<Holder>>(
            `${this.holdersUrl(participantContextId)}/${this.segment(holderId)}`,
            { headers: this.headers(options) }
        );
    }

    queryHolders(
        participantContextId: string,
        query: QuerySpec | null = null,
        options?: IssuerRequestOptions
    ): Observable<ApiResponse<Holder[]>> {
        return this.http.post<ApiResponse<Holder[]>>(
            `${this.holdersUrl(participantContextId)}/query`,
            query,
            { headers: this.headers(options) }
        );
    }

    deleteHolderById(
        participantContextId: string,
        holderId: string,
        options?: IssuerRequestOptions
    ): Observable<ApiResponse<void>> {
        return this.http.delete<ApiResponse<void>>(
            `${this.holdersUrl(participantContextId)}/${this.segment(holderId)}`,
            this.httpOptions(options)
        );
    }

    createAttestation(
        participantContextId: string,
        request: IssuerAttestationDefinition,
        options?: IssuerRequestOptions
    ): Observable<ApiResponse<IssuerAttestationDefinition>> {
        return this.http.post<ApiResponse<IssuerAttestationDefinition>>(
            `${this.participantUrl(participantContextId)}/attestations`,
            request,
            this.httpOptions(options)
        );
    }

    getAllAttestations(
        participantContextId: string,
        options?: IssuerRequestOptions
    ): Observable<ApiResponse<IssuerAttestationDefinition[]>> {
        return this.http.get<ApiResponse<IssuerAttestationDefinition[]>>(
            `${this.participantUrl(participantContextId)}/attestations`,
            { headers: this.headers(options) }
        );
    }

    mergeAttestationConfiguration(
        participantContextId: string,
        attestationId: string,
        holderId: string,
        claims: JsonObject,
        options?: IssuerRequestOptions
    ): Observable<ApiResponse<IssuerAttestationDefinition>> {
        return this.http.put<ApiResponse<IssuerAttestationDefinition>>(
            `${this.participantUrl(participantContextId)}/attestations/${this.segment(attestationId)}/configuration`,
            { holderId, claims },
            this.httpOptions(options)
        );
    }

    queryCredentials(
        participantContextId: string,
        query: QuerySpec | null = null,
        options?: IssuerRequestOptions
    ): Observable<ApiResponse<IssuerCredentialResource[]>> {
        return this.http.post<ApiResponse<IssuerCredentialResource[]>>(
            `${this.credentialsUrl(participantContextId)}/query`,
            query,
            { headers: this.headers(options) }
        );
    }

    queryTrackedCredentials(
        participantContextId: string,
        query: QuerySpec | null = null,
        options?: IssuerRequestOptions
    ): Observable<ApiResponse<VerifiableCredentialResource[]>> {
        return this.http.post<ApiResponse<VerifiableCredentialResource[]>>(
            `${this.credentialsUrl(participantContextId)}/tracked/query`,
            query,
            { headers: this.headers(options) }
        );
    }

    getCredentialStatus(
        participantContextId: string,
        credentialId: string,
        options?: IssuerRequestOptions
    ): Observable<ApiResponse<IssuerCredentialStatus>> {
        return this.http.get<ApiResponse<IssuerCredentialStatus>>(
            `${this.credentialsUrl(participantContextId)}/${this.segment(credentialId)}/status`,
            { headers: this.headers(options) }
        );
    }

    createCredentialOffer(
        participantContextId: string,
        request: CredentialOffer,
        options?: IssuerRequestOptions
    ): Observable<ApiResponse<void>> {
        return this.http.post<ApiResponse<void>>(
            `${this.credentialsUrl(participantContextId)}/offer`,
            request,
            this.httpOptions(options)
        );
    }

    revokeCredential(
        participantContextId: string,
        credentialId: string,
        options?: IssuerRequestOptions
    ): Observable<ApiResponse<void>> {
        return this.credentialLifecycleRequest(
            participantContextId,
            credentialId,
            'revoke',
            options
        );
    }

    suspendCredential(
        participantContextId: string,
        credentialId: string,
        options?: IssuerRequestOptions
    ): Observable<ApiResponse<void>> {
        return this.credentialLifecycleRequest(
            participantContextId,
            credentialId,
            'suspend',
            options
        );
    }

    resumeCredential(
        participantContextId: string,
        credentialId: string,
        options?: IssuerRequestOptions
    ): Observable<ApiResponse<void>> {
        return this.credentialLifecycleRequest(
            participantContextId,
            credentialId,
            'resume',
            options
        );
    }

    revokeTrackedCredential(
        participantContextId: string,
        credentialId: string,
        reason: string,
        options?: IssuerRequestOptions
    ): Observable<ApiResponse<VerifiableCredentialResource>> {
        return this.trackedCredentialLifecycleRequest(
            participantContextId,
            credentialId,
            'revoke',
            { reason },
            options
        );
    }

    suspendTrackedCredential(
        participantContextId: string,
        credentialId: string,
        reason: string,
        options?: IssuerRequestOptions
    ): Observable<ApiResponse<VerifiableCredentialResource>> {
        return this.trackedCredentialLifecycleRequest(
            participantContextId,
            credentialId,
            'suspend',
            { reason },
            options
        );
    }

    resumeTrackedCredential(
        participantContextId: string,
        credentialId: string,
        options?: IssuerRequestOptions
    ): Observable<ApiResponse<VerifiableCredentialResource>> {
        return this.trackedCredentialLifecycleRequest(
            participantContextId,
            credentialId,
            'resume',
            null,
            options
        );
    }

    getIssuerMetadata(
        participantContextId: string,
        options?: IssuerRequestOptions
    ): Observable<ApiResponse<IssuerMetadata>> {
        return this.http.get<ApiResponse<IssuerMetadata>>(
            `${this.participantUrl(participantContextId)}/metadata`,
            { headers: this.headers(options) }
        );
    }

    requestCredentials(
        participantContextId: string,
        request: CredentialRequestMessage | null,
        options?: IssuerRequestOptions
    ): Observable<ApiResponse<CredentialRequestStatus>> {
        return this.http.post<ApiResponse<CredentialRequestStatus>>(
            `${this.participantUrl(participantContextId)}/credentials`,
            request,
            { headers: this.headers(options) }
        );
    }

    getCredentialRequestStatus(
        participantContextId: string,
        credentialRequestId: string,
        options?: IssuerRequestOptions
    ): Observable<ApiResponse<CredentialRequestStatus>> {
        return this.http.get<ApiResponse<CredentialRequestStatus>>(
            `${this.participantUrl(participantContextId)}/requests/${this.segment(credentialRequestId)}`,
            { headers: this.headers(options) }
        );
    }

    private credentialLifecycleRequest(
        participantContextId: string,
        credentialId: string,
        action: 'revoke' | 'suspend' | 'resume',
        options?: IssuerRequestOptions
    ): Observable<ApiResponse<void>> {
        return this.http.post<ApiResponse<void>>(
            `${this.credentialsUrl(participantContextId)}/${this.segment(credentialId)}/${action}`,
            null,
            this.httpOptions(options)
        );
    }

    private trackedCredentialLifecycleRequest(
        participantContextId: string,
        credentialId: string,
        action: 'revoke' | 'suspend' | 'resume',
        body: { reason: string } | null,
        options?: IssuerRequestOptions
    ): Observable<ApiResponse<VerifiableCredentialResource>> {
        return this.http.post<ApiResponse<VerifiableCredentialResource>>(
            `${this.credentialsUrl(participantContextId)}/tracked/${this.segment(credentialId)}/${action}`,
            body,
            this.httpOptions(options)
        );
    }

    private credentialDefinitionsUrl(participantContextId: string): string {
        return `${this.participantUrl(participantContextId)}/credentialdefinitions`;
    }

    private holdersUrl(participantContextId: string): string {
        return `${this.participantUrl(participantContextId)}/holders`;
    }

    private credentialsUrl(participantContextId: string): string {
        return `${this.participantUrl(participantContextId)}/credentials`;
    }

    private participantUrl(participantContextId: string): string {
        return `${this.apiBase}/participants/${this.segment(participantContextId)}`;
    }

    private segment(value: string): string {
        return encodeURIComponent(value);
    }

    private headers(options?: IssuerRequestOptions): HttpHeaders {
        let headers = new HttpHeaders();
        if (options?.apiKey) {
            headers = headers.set('x-api-key', options.apiKey);
        }
        if (options?.authorization) {
            headers = headers.set('Authorization', options.authorization);
        }
        return headers;
    }

    private httpOptions(options?: IssuerRequestOptions): {
        headers: HttpHeaders;
        params: HttpParams;
    } {
        let params = new HttpParams();
        if (options?.issuerOrganisationId != null) {
            params = params.set(
                'issuerOrganisationId',
                String(options.issuerOrganisationId)
            );
        }
        return { headers: this.headers(options), params };
    }
}
